import React, { useState } from "react"

import { useInView } from "react-intersection-observer"
import { useInfiniteQuery } from "react-query"
import { useParams } from "react-router-dom"

import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import CloseIcon from "@mui/icons-material/Close"
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined"
import { Card, Grid, IconButton, Skeleton } from "@mui/material"
import { Button, type InputChangeEvent, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { type MenuOption, SelectWithSearch } from "../../components/form/SelectWithSearch/SelectWithSearch"
import { getTheme } from "../../hooks/UseTheme"
import { KonanAPI } from "../../services/KonanAPI"
import type { PaginatedScoreCardListList } from "../../types/generated/api/PaginatedScoreCardListList"
import type { ScoreCardList } from "../../types/generated/api/ScoreCardList"
import { extractPageFromBackEndPaginationLink } from "../../utils/genericHelpers"

import styles from "./ScorecardSet.module.scss"

// type for URL params
type ParamsType = {
  id: string
}

interface ScoreCardNodeType {
  scorecard?: { name: string; uuid: string | undefined }
  isEdit?: boolean
  menuView?: boolean
  isLoading?: boolean
  handleRemove?: () => void
  onValueChange?: (scorecard: { name: string; uuid: string }) => void
  usedScorecardsIds?: Set<string | undefined>
}

/**
 * ScoreCardCard component
 * @param {string} scorecard
 * @param {boolean} isEdit
 * @param {boolean} menuView
 * @param {boolean} isLoading
 * @param {function} handleRemove
 * @param {function} onValueChange select value change
 * @param {Set<string | undefined>} usedScorecardsIds
 * @returns {React.ReactElement}
 */
export function ScoreCardBlock(props: Readonly<ScoreCardNodeType>): React.ReactElement {
  const { isEdit, handleRemove, scorecard, menuView, isLoading, onValueChange, usedScorecardsIds } = props
  const { id: projectId } = useParams<ParamsType>()

  const readMode = isEdit ?? null

  const [searchValue, setSearchValue] = useState<string | undefined>(undefined)

  const theme = getTheme()

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastScoreCardRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isLoading && !isFetchingNextPage && fetchNextPage()
    },
  })

  // fetch project labels
  const {
    data: scorecards,
    isLoading: isLoadingScorecards,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedScoreCardListList>, AxiosError, ScoreCardList>(
    ["scorecards", projectId, searchValue],
    ({ pageParam = 1 }) => KonanAPI.fetchScorecards(projectId as string, pageParam, 20, searchValue),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce<ScoreCardList[]>(
          (accumulator, page) => accumulator.concat(page.data.results as ScoreCardList[]),
          [],
        ),
      }),
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!projectId && isEdit,
      refetchOnMount: true,
    },
  )

  const optionsList = (scorecards?.pages ?? [])?.reduce<MenuOption[]>((accumulator, scorecard) => {
    if (!usedScorecardsIds?.has(scorecard.uuid)) {
      accumulator.push({
        text: scorecard.name,
        value: scorecard.uuid,
        item: <Typography variant="p">{scorecard.name}</Typography>,
      })
    }

    return accumulator
  }, [])

  return (
    <Card className={menuView ? styles.ScoreCardBlock_menuView : styles.ScoreCardBlock}>
      <Grid container wrap="nowrap">
        <Grid item>
          <Grid item className={styles.ScoreCardBlockType}>
            <AssignmentOutlinedIcon sx={{ color: theme.palette.base.text[1] }} />
          </Grid>
        </Grid>
        <Grid
          item
          display={"flex"}
          flexGrow={1}
          flexDirection={"column"}
          paddingLeft={1}
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Grid item container justifyContent="space-between">
            <Grid item>
              <Typography variant="label-bold" variantColor={2}>
                SCORECARD
              </Typography>
            </Grid>
            {readMode && (
              <Grid item justifySelf={"end"}>
                <IconButton style={{ marginTop: "-15px" }} size="small" onClick={() => handleRemove?.()}>
                  <CloseIcon fontSize="small" className={styles.remove} />
                </IconButton>
              </Grid>
            )}
          </Grid>
          <Grid
            item
            xs={12}
            container
            alignItems="flex-start"
            justifyContent={"space-between"}
            marginTop={0.5}
            wrap="nowrap"
          >
            <Grid item>
              {isLoading ? (
                <Skeleton height={"25px"} width="100px" />
              ) : isEdit || menuView ? (
                <SelectWithSearch
                  menuTriggerProps={{
                    placeholder: "Choose scorecard",
                    disabled: menuView || scorecards?.pages?.length === 0,
                    fullWidth: true,
                    hideDescription: true,
                  }}
                  optionsList={optionsList}
                  isOptionsLoading={isLoadingScorecards || isFetchingNextPage}
                  inputValue={scorecard?.uuid?.startsWith("default-") ? "Choose scorecard" : scorecard?.name}
                  onSelectMenuItem={({ value, text }) => onValueChange?.({ uuid: value, name: text })}
                  searchInputChangeHandler={(e: InputChangeEvent) => {
                    setSearchValue(e.target.value as string)
                  }}
                  onMenuClose={() => {
                    setSearchValue(undefined)
                  }}
                  lastMenuItemRef={lastScoreCardRef}
                />
              ) : (
                <Typography variant="h3-bold" noWrap>
                  {scorecard?.name}
                </Typography>
              )}
            </Grid>

            <Grid item display={"flex"} alignSelf={"center"} justifySelf={"end"} ml={1}>
              <Button
                variant="secondary"
                /*Workaround to use window.open in safari */
                onClick={() =>
                  setTimeout(() => {
                    window.open(`/projects/${projectId}/Scorecards`, "_blank")
                  })
                }
                size="small"
                disabled={menuView}
              >
                <LaunchOutlinedIcon
                  fontSize="medium"
                  sx={{ color: menuView ? theme.palette.gray.background[1] : theme.palette.base.text[1] }}
                />
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  )
}
